/**
 * Копирование в буфер обмена — единственный производитель этого эффекта.
 *
 * Зачем helper, а не `navigator.clipboard` на местах. `navigator.clipboard` существует
 * ТОЛЬКО в защищённом контексте (https или localhost) — это спецификация, а не каприз
 * браузера. Стенд wn-227 и часть операторских установок работают по http, и там объект
 * `undefined`. Живой замер 2026-09-02 показал два разных дефекта из одного корня:
 *
 *  - `CustomerPingCard`: `if (navigator.clipboard)` без ветки else — кнопка «Копировать IP»
 *    нажимается и МОЛЧА не делает ничего;
 *  - `PingResultsPanel`: вызов без проверки, а следом `message.success('скопировано')` —
 *    оператору сообщают об успехе, которого не было.
 *
 * Функция возвращает результат, чтобы вызывающий показал ЧЕСТНУЮ обратную связь, а не
 * успех по факту нажатия.
 */

export type CopyResult = 'copied' | 'unavailable' | 'failed';

/**
 * Возвращает `copied` при успехе; `unavailable` — если ни один способ не доступен
 * (не защищённый контекст и запрещён legacy-путь); `failed` — если способ был, но отказал.
 */
export async function copyToClipboard(text: string): Promise<CopyResult> {
  if (!text) return 'failed';

  // Основной путь: доступен только в защищённом контексте.
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return 'copied';
    } catch {
      // Отказ разрешения или отсутствие фокуса — пробуем запасной путь ниже.
    }
  }

  // Запасной путь для http: устаревший, но работающий в этом случае execCommand.
  // Не «шим на всякий случай»: это ЕДИНСТВЕННЫЙ способ скопировать в незащищённом
  // контексте, а именно там сейчас живёт стенд.
  if (typeof document === 'undefined') return 'unavailable';
  try {
    const area = document.createElement('textarea');
    area.value = text;
    // Вне потока и невидимо, но в DOM — иначе выделение не сработает.
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.top = '-1000px';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(area);
    return ok ? 'copied' : 'failed';
  } catch {
    return 'unavailable';
  }
}
